import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, categories } from '@/mocks/anime';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const article = articles.find((a) => a.id === id);
  const category = article ? categories.find((c) => c.slug === article.categorySlug) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <main className="pt-20 pb-10 bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <p className="text-foreground-400 text-sm mb-4">文章未找到</p>
          <Link to="/" className="text-primary-400 hover:text-primary-300 text-sm transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const articleUrl = `https://www.51anime.com/article/${article.id}`;

  return (
    <main className="pt-20 pb-10 md:pb-14 bg-background-50 min-h-screen">
      <SEO
        title={`${article.title} - 51动漫官网`}
        description={article.excerpt}
        keywords={`51动漫官网,51动漫,${article.tags.join(',')},${article.category},51动漫网,国产动漫`}
        canonicalUrl={articleUrl}
        ogType="article"
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51anime.com/' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.51anime.com/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.coverImage,
        url: articleUrl,
        publisher: {
          '@type': 'Organization',
          name: '51动漫官网',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
      }} />

      <article className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          {category && (
            <>
              <Link to={`/category/${category.slug}`} className="hover:text-primary-400 transition-colors cursor-pointer">
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </>
          )}
          <span className="text-foreground-300 line-clamp-1">{article.title}</span>
        </nav>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2 py-1 text-xs font-medium bg-primary-500/15 text-primary-400 rounded-md">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
            <span className="text-xs text-foreground-500">·</span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
            <span className="text-xs text-foreground-500">·</span>
            <span className="text-xs text-foreground-500">作者：{article.author}</span>
          </div>
          <h1 className="text-xl md:text-3xl font-bold text-foreground-50 font-heading leading-snug mb-4">
            {article.title}
          </h1>
          <div className="flex items-center gap-1">
            <span className="text-sm text-secondary-500 font-semibold">{article.rating}</span>
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`text-xs ${
                    star <= Math.round(article.rating / 2)
                      ? 'ri-star-fill text-secondary-500'
                      : 'ri-star-line text-foreground-600'
                  }`}
                ></i>
              ))}
            </div>
          </div>
        </div>

        <div className="relative h-56 md:h-80 rounded-lg overflow-hidden mb-8">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${article.coverImage})` }}
          />
        </div>

        <div
          className="prose prose-invert max-w-none mb-8 text-foreground-200 text-sm md:text-base leading-relaxed space-y-4"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="flex items-center gap-2 flex-wrap mb-8 pt-4 border-t border-background-200/60">
          <span className="text-sm text-foreground-500">标签：</span>
          {article.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-400 rounded-full border border-background-200/60">
              #{tag}
            </span>
          ))}
        </div>

        {relatedArticles.length > 0 && (
          <div className="pt-6 border-t border-background-200/60">
            <h2 className="text-lg font-bold text-foreground-100 font-heading mb-4">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group p-3 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-500/20 transition-all duration-200 cursor-pointer"
                >
                  <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2">
                    {ra.title}
                  </h3>
                  <span className="text-xs text-foreground-500">{ra.publishDate}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </main>
  );
}